import Vue from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import i18n, {timeFormat} from '@/i18n';
import store from '@/store';
import {isDateOnly, parseDateValue} from '@/services/dateValue';
import {formatAmount} from '@/utils/amountFormatter';

dayjs.locale(i18n.locale);
dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
Vue.prototype.$dayjs = dayjs;

// A calendar date is the same day everywhere, so converting one is always wrong. Guarding here
// rather than at each call site makes the safe behaviour the default: three rounds of appending
// skipTimezone=true to individual filters (7d768df8, bb3aa629, cea5fd61) each left the next missed
// site to be found by a customer. See services/dateValue.js for how the two kinds are told apart.
const getDayjsWithTimezone = (value, skipTimezone = false) => {
  const date = parseDateValue(value);
  if (isDateOnly(value)) return date;

  if (!skipTimezone) {
    const userTimezone = store.getters.user?.settings?.timezone;
    if (userTimezone) {
      return date.utc(true)
        .tz(userTimezone);
    }
  }
  return date;
};

const getDayjsWithUserZone = (value, skipTimezone = false) => {
  const date = parseDateValue(value);
  if (isDateOnly(value)) return date;

  if (!skipTimezone) {
    const userTimezone = store.getters.user?.settings?.timezone;
    if (userTimezone) {
      return date.utc(true)
        .tz(userTimezone);
    }
    // Fallback: convert UTC to browser local time (original behavior)
    return date.utc(true).local();
  }
  return date;
};

Vue.filter('date', (value, skipTimezone = false) => {
  if (!value) return value;
  return getDayjsWithTimezone(value, skipTimezone)
    .format('L');
});
Vue.filter('formatDate', (value, skipTimezone = false) => {
  if (!value) return value;
  return getDayjsWithTimezone(value, skipTimezone)
    .format('DD.MM.YYYY');
});
Vue.filter('time', (value, skipTimezone = false) => {
  if (!value) return value;
  return getDayjsWithTimezone(value, skipTimezone)
    .format('LT');
});

Vue.filter('formatTime', (value, skipTimezone = false) => {
  if (!value) return value;
  return getDayjsWithTimezone(value, skipTimezone)
    .format(timeFormat[i18n.locale]);
});
Vue.filter('timeFilter', (value, skipTimezone = false) => {
  if (!value) return value;
  return getDayjsWithTimezone(value, skipTimezone)
    .format(timeFormat[i18n.locale]);
});

Vue.filter('dateUserZone', (value, skipTimezone = false) => {
  if (!value) return value;
  return getDayjsWithUserZone(value, skipTimezone)
    .format('L');
});

Vue.filter('timeUserZone', (value, skipTimezone = false) => {
  if (!value) return value;
  return getDayjsWithUserZone(value, skipTimezone)
    .format('LT');
});

Vue.filter('dateTime', (value, skipTimezone = false) => {
  if (!value) return value;
  return getDayjsWithTimezone(value, skipTimezone)
    .format('L LT');
});

Vue.filter('dateTimeUserZone', (value, skipTimezone = false) => {
  if (!value) return value;
  return getDayjsWithUserZone(value, skipTimezone)
    .format('L LT');
});
Vue.filter('getFileNameFromUrl', (url) => {
  const parsedUrl = new URL(url);
  return parsedUrl.pathname.split('/')
    .pop();
});
Vue.filter('formatShortDate', (value, skipTimezone = false) => {
  if (!value) return value;
  return getDayjsWithTimezone(value, skipTimezone)
    .format('MMM-YY');
});

// How far off schedule this installment is, in days, whichever way it is off.
//
// Both numbers come from the server, never from arithmetic here: each compares a stored timestamp
// against a calendar day, and doing that in the browser is how the two end up disagreeing across a
// timezone.
//
// `daysLate` and `daysOverdue` answer the same question against different endpoints -- the
// collection date for a settled row, today for one still owed -- and a row can only be one or the
// other, so they were never both filled. Two columns meant one was always blank and the reader had
// to know which to look at. Signed for a settled row so an early collection reads as early; plain
// for an overdue one, where the sign would say nothing.
Vue.filter('daysOffSchedule', (row) => {
  if (!row) return '—';
  if (Number.isInteger(row.daysLate)) return row.daysLate > 0 ? `+${row.daysLate}` : String(row.daysLate);
  if (Number.isInteger(row.daysOverdue) && row.daysOverdue > 0) return String(row.daysOverdue);
  return '—';
});

// Separators follow the provider's base currency; see utils/amountFormatter.js.
Vue.filter('formatAmount', (value) => {
  if (value === null || value === undefined || value === '') return '0';
  const formatted = formatAmount(value);
  return formatted === '' ? value : formatted;
});
